/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, trackCrossPlatform } from "@packages/analytics";
import type { ThemeType } from "@packages/ui";
import { constantSemanticColors, spacing } from "@packages/ui";
import { DataTestId } from "@tools/playwright/global/types/enums/dataTestId.enum";
import { memo, useCallback } from "react";

import type { VttSegment } from "../../../utils/vttParser";
import type { TranscriptTheme } from "./TranscriptSettingsContext";
import { useTranscriptDisplay } from "./TranscriptSettingsContext";

const segmentStyle: Interpolation = {
  display: "block",
  paddingBlock: spacing.xxs,
};
const textStyle: Interpolation = {
  lineHeight: 1.6,
  transition: "color 250ms ease",
  whiteSpace: "pre-line",
  willChange: "color",
};
const seenStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHighest,
});
const unseenStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedLow,
});
const seenColorModeStyle: Interpolation = {
  color: constantSemanticColors.onColorHighest,
};
const unseenColorModeStyle: Interpolation = {
  color: constantSemanticColors.fixedLightenMedLow,
};

interface SegmentItemProps {
  segment: VttSegment;
  currentTime: number;
  theme: TranscriptTheme;
  showAllBlocksAsSeen: boolean;
  onPointerUp: () => void;
}

const SegmentItem = memo(
  ({
    segment,
    currentTime,
    theme,
    showAllBlocksAsSeen,
    onPointerUp,
  }: SegmentItemProps) => {
    const isActive =
      currentTime >= segment.startTime && currentTime <= segment.endTime;
    const hasBeenSeen = currentTime > segment.endTime;

    return (
      <span
        key={segment.id}
        id={segment.id}
        css={segmentStyle}
        onPointerUp={onPointerUp}
      >
        <span
          css={[
            textStyle,
            isActive || hasBeenSeen || showAllBlocksAsSeen
              ? theme === "color"
                ? seenColorModeStyle
                : seenStyle
              : theme === "color"
                ? unseenColorModeStyle
                : unseenStyle,
          ]}
          data-testid={`${DataTestId.transcriptVttBlock}-${segment.id}`}
        >
          {segment.text}
        </span>
      </span>
    );
  },
  (prevProps, nextProps) => {
    const prevActive =
      prevProps.currentTime >= prevProps.segment.startTime &&
      prevProps.currentTime <= prevProps.segment.endTime;
    const nextActive =
      nextProps.currentTime >= nextProps.segment.startTime &&
      nextProps.currentTime <= nextProps.segment.endTime;

    const prevSeen = prevProps.currentTime > prevProps.segment.endTime;
    const nextSeen = nextProps.currentTime > nextProps.segment.endTime;

    return (
      prevActive === nextActive &&
      prevSeen === nextSeen &&
      prevProps.segment.id === nextProps.segment.id &&
      prevProps.theme === nextProps.theme &&
      prevProps.showAllBlocksAsSeen === nextProps.showAllBlocksAsSeen &&
      prevProps.onPointerUp === nextProps.onPointerUp
    );
  },
);

interface VttSegmentRendererProps {
  segments: VttSegment[];
  currentTime: number;
}

export const VttSegmentRenderer = ({
  segments,
  currentTime,
}: VttSegmentRendererProps) => {
  const { theme, showAllBlocksAsSeen } = useTranscriptDisplay();

  const handleSegmentPointerUp = useCallback(
    () =>
      trackCrossPlatform({
        event: { event: HallowAnalyticsEvent.TappedTranscriptText },
      }),
    [],
  );

  return (
    <>
      {segments.map((segment) => (
        <SegmentItem
          key={segment.id}
          segment={segment}
          currentTime={currentTime}
          theme={theme}
          showAllBlocksAsSeen={showAllBlocksAsSeen}
          onPointerUp={handleSegmentPointerUp}
        />
      ))}
    </>
  );
};
